import { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Stack,
    Autocomplete,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    ListItemText,
    Alert
} from '@mui/material';
import {
    Add,
    FilterList,
    Clear as ClearIcon,
    AdminPanelSettings as AdminIcon,
    Person as PersonIcon,
    CheckCircle as CheckIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircleOutline as EnableIcon,
    Key as KeyIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { userAPI, metaAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { useRTL } from '../../../hooks/useRTL';
import NoPrivilege from '../../../components/common/NoPrivilege';
import NoContent from '../../../components/common/NoContent';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import UsersTable from './UsersTable';
import PrivilegeDialog from '../../../components/admin/PrivilegeDialog';
import ChangePasswordDialog from '../../../components/changePasswordDialoge';
import AddUserDialog from './AddUserDialog'; // You'll need to create this

const PrivilegeManager = () => {
    const { t } = useTranslation();
    const { isRTL, cardGridProps, iconContainerProps, textContainerProps } = useRTL();
    const { user: currentUser, isSuperAdmin, updateUser } = useAuth();

    // Status options for filter
    const STATUS_OPTIONS = [
        { value: '', label: t('common.all', 'All') },
        { value: 'active', label: t('common.active', 'Active') },
        { value: 'inactive', label: t('common.inactive', 'Inactive') }
    ];

    const USER_TYPE_OPTIONS = [
        { value: '', label: t('common.all', 'All') },
        { value: 'superAdmin', label: t('common.superAdmin', 'Super Admin') },
        { value: 'user', label: t('common.regularUser', 'Regular User') }
    ];

    // Data State
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [privilegeModules, setPrivilegeModules] = useState({});
    const [branches, setBranches] = useState([]);
    const [privilegeError, setPrivilegeError] = useState(false);
    const [totalUsers, setTotalUsers] = useState(0);

    // Export State
    const [exporting, setExporting] = useState(false);

    // Pagination State
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Dialog State
    const [selectedUser, setSelectedUser] = useState(null);
    const [isPrivilegeDialogOpen, setPrivilegeDialogOpen] = useState(false);
    const [isPasswordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [isAddUserDialogOpen, setAddUserDialogOpen] = useState(false);

    // Confirmation Dialog State
    const [openConfirm, setOpenConfirm] = useState(false);
    const [confirmDetails, setConfirmDetails] = useState({
        title: '',
        message: '',
        onConfirm: null,
        confirmText: '',
        loading: false
    });

    // Menu State
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);

    // Consolidated filter state
    const [filters, setFilters] = useState({
        status: '',
        userType: '',
        branch: null,
        search: ''
    });

    // Permissions
    const canView = isSuperAdmin || currentUser?.privileges?.some(
        p => p.module === 'privileges' && p.operations.includes('view')
    );
    const canEdit = isSuperAdmin || currentUser?.privileges?.some(
        p => p.module === 'privileges' && p.operations.includes('update')
    );
    const canCreate = isSuperAdmin || currentUser?.privileges?.some(
        p => p.module === 'users' && p.operations.includes('create')
    );
    const canDelete = isSuperAdmin;
    const canExport = canView;

    // Fetch users with pagination and filters
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setPrivilegeError(false);
        setError('');

        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage
            };

            if (filters.status) {
                params.isActive = filters.status === 'active';
            }
            if (filters.userType) {
                params.userType = filters.userType;
            }
            if (filters.branch) {
                params.branch = filters.branch._id;
            }
            if (filters.search) {
                params.search = filters.search;
            }

            const response = await userAPI.getAll(params);
            const responseData = response.data;
            const usersArr = responseData.data || [];
            const paginationData = responseData.pagination || {};

            const formattedUsers = usersArr.map(u => ({
                ...u,
                id: u._id || u.id
            }));

            setUsers(formattedUsers);
            setTotalUsers(paginationData.total || 0);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Error fetching users';
            if (err.response?.status === 403 || errorMessage.toLowerCase().includes('insufficient privileges')) {
                setPrivilegeError(true);
            } else {
                setError(errorMessage);
                toast.error(errorMessage);
            }
            setUsers([]);
            setTotalUsers(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, filters]);

    // Fetch dropdown data
    const fetchDropdownData = useCallback(async () => {
        try {
            const [modulesResponse, branchesArr] = await Promise.all([
                metaAPI.getPrivilegeModules(),
                metaAPI.getBranches()
            ]);

            let modules = modulesResponse.data.data || modulesResponse.data;
            if (Array.isArray(modules)) {
                modules = modules.reduce((acc, mod) => {
                    if (mod && mod.name) acc[mod.name] = mod;
                    return acc;
                }, {});
            }

            setPrivilegeModules(modules || {});
            setBranches(branchesArr || []);
        } catch (err) {
            toast.error(t('common.fetchMetaError', 'Failed to fetch required data'));
        }
    }, [t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchDropdownData();
    }, [fetchDropdownData]);

    // Filter handlers
    const handleFilterChange = useCallback((key, value) => {
        setPage(0);
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setPage(0);
        setFilters({
            status: '',
            userType: '',
            branch: null,
            search: ''
        });
    }, []);

    // Check if any filter is active
    const hasActiveFilters = filters.status || filters.userType || filters.branch || filters.search;

    // Export handler
    const handleExport = async () => {
        setExporting(true);
        try {
            const params = {};
            if (filters.status) params.isActive = filters.status === 'active';
            if (filters.userType) params.userType = filters.userType;
            if (filters.branch) params.branch = filters.branch._id;
            if (filters.search) params.search = filters.search;

            const response = await userAPI.export(params);

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const date = new Date().toISOString().split('T')[0];
            link.download = `users_export_${date}.xlsx`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(t('users.exportSuccess', 'Users exported successfully'));
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.message || t('users.exportError', 'Failed to export users'));
        } finally {
            setExporting(false);
        }
    };

    // Dialog handlers
    const handleOpenPrivilegeDialog = useCallback((user) => {
        if (!canEdit) {
            toast.error(t('privileges.noEditPermission', 'You do not have permission to manage privileges'));
            return;
        }
        setSelectedUser(user);
        setPrivilegeDialogOpen(true);
    }, [canEdit, t]);

    const handleClosePrivilegeDialog = useCallback((shouldRefresh) => {
        setPrivilegeDialogOpen(false);
        setSelectedUser(null);
        if (shouldRefresh) {
            fetchUsers();
        }
    }, [fetchUsers]);

    const handleOpenPasswordDialog = useCallback((user) => {
        setSelectedUser(user);
        setPasswordDialogOpen(true);
        setAnchorEl(null);
    }, []);

    const handleClosePasswordDialog = useCallback((shouldRefresh) => {
        setPasswordDialogOpen(false);
        setSelectedUser(null);
        if (shouldRefresh) {
            toast.info(t('users.passwordResetNote', 'User will need to use the new password on next login'));
        }
    }, [t]);

    const handleOpenAddUserDialog = useCallback(() => {
        setAddUserDialogOpen(true);
    }, []);

    const handleCloseAddUserDialog = useCallback((shouldRefresh) => {
        setAddUserDialogOpen(false);
        if (shouldRefresh) {
            fetchUsers();
        }
    }, [fetchUsers]);

    // Menu handlers
    const handleMenuOpen = useCallback((event, user) => {
        setAnchorEl(event.currentTarget);
        setMenuUser(user);
    }, []);

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    // Action handlers with confirmation
    const triggerConfirm = useCallback((title, message, onConfirm, confirmText = t('common.confirm', 'Confirm')) => {
        setConfirmDetails({ title, message, onConfirm, confirmText, loading: false });
        setOpenConfirm(true);
    }, [t]);

    const handleDelete = useCallback((user) => {
        handleMenuClose();
        triggerConfirm(
            t('users.deleteConfirmTitle', 'Delete User'),
            t('users.deleteConfirmMessage', {
                username: user?.username,
                defaultValue: `Are you sure you want to delete user "${user?.username}"? This action cannot be undone.`
            }),
            async () => {
                setConfirmDetails(prev => ({ ...prev, loading: true }));
                try {
                    await userAPI.delete(user._id || user.id);
                    toast.success(t('users.deleteSuccess', 'User deleted successfully'));
                    fetchUsers();
                } catch (err) {
                    toast.error(err.response?.data?.message || t('users.deleteError', 'Failed to delete user'));
                } finally {
                    setConfirmDetails(prev => ({ ...prev, loading: false }));
                    setOpenConfirm(false);
                    setMenuUser(null);
                }
            },
            t('common.delete', 'Delete')
        );
    }, [handleMenuClose, triggerConfirm, t, fetchUsers]);

    const handleToggleStatus = useCallback((user) => {
        handleMenuClose();
        const isActive = user?.isActive;
        triggerConfirm(
            isActive
                ? t('users.disableConfirmTitle', 'Disable User')
                : t('users.enableConfirmTitle', 'Enable User'),
            isActive
                ? t('users.disableConfirmMessage', {
                    username: user?.username,
                    defaultValue: `Are you sure you want to disable user "${user?.username}"? They will not be able to log in.`
                })
                : t('users.enableConfirmMessage', {
                    username: user?.username,
                    defaultValue: `Are you sure you want to enable user "${user?.username}"? They will be able to log in.`
                }),
            async () => {
                setConfirmDetails(prev => ({ ...prev, loading: true }));
                try {
                    await userAPI.update(user._id || user.id, { isActive: !isActive });
                    toast.success(
                        !isActive
                            ? t('users.enableSuccess', 'User enabled successfully')
                            : t('users.disableSuccess', 'User disabled successfully')
                    );
                    fetchUsers();
                } catch (err) {
                    toast.error(err.response?.data?.message || t('users.statusUpdateError', 'Failed to update user status'));
                } finally {
                    setConfirmDetails(prev => ({ ...prev, loading: false }));
                    setOpenConfirm(false);
                    setMenuUser(null);
                }
            },
            isActive ? t('common.disable', 'Disable') : t('common.enable', 'Enable')
        );
    }, [handleMenuClose, triggerConfirm, t, fetchUsers]);

    // Branch change handler
    const handleBranchChange = useCallback(async (user, branchId) => {
        try {
            const res = await userAPI.update(user.id || user._id, { branch: branchId });
            toast.success(t('users.branchAssigned', { username: user.username, defaultValue: `Branch assigned to ${user.username}` }));
            fetchUsers();

            if (currentUser.id === user.id || currentUser._id === user._id) {
                updateUser(res.data.data);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t('users.branchAssignError', 'Failed to assign branch'));
        }
    }, [t, fetchUsers, currentUser, updateUser]);

    // Pagination handlers
    const handlePageChange = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Helper functions
    const isUserSuperAdmin = useCallback((user) => {
        return user?.isSuperAdmin || user?.userType === 'superAdmin';
    }, []);

    const getPrivilegeCount = useCallback((user) => user?.privileges?.length || 0, []);

    // Statistics calculations
    const stats = {
        total: totalUsers,
        superAdmins: users.filter(u => isUserSuperAdmin(u)).length,
        activeUsers: users.filter(u => u.isActive).length,
        avgPrivileges: users.length > 0
            ? Math.round(users.reduce((sum, u) => sum + getPrivilegeCount(u), 0) / users.length)
            : 0
    };

    // Permission check
    if (!canView || privilegeError) {
        return <NoPrivilege message={t('privileges.noPrivilegeError', 'You do not have permission to view this page')} />;
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" component="h1" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        {t('privilegeManagement', 'Privilege Management')}
                    </Typography>
                    {!loading && (
                        <Chip
                            label={`${totalUsers} ${t('common.total', 'total')}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Box>
                <Stack direction="row" spacing={1}>
                    {canExport && (
                        <Button
                            variant="outlined"
                            startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={exporting || loading}
                        >
                            {exporting ? t('common.exporting', 'Exporting...') : t('common.export', 'Export')}
                        </Button>
                    )}
                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenAddUserDialog}
                        >
                            {t('users.add', 'Add User')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={cardGridProps}>
                                <Box sx={textContainerProps}>
                                    <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('common.totalUsers', 'Total Users')}
                                    </Typography>
                                    <Typography variant="h4" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {stats.total}
                                    </Typography>
                                </Box>
                                <Box sx={iconContainerProps}>
                                    <PersonIcon />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={cardGridProps}>
                                <Box sx={textContainerProps}>
                                    <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('common.superAdmins', 'Super Admins')}
                                    </Typography>
                                    <Typography variant="h4" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {stats.superAdmins}
                                    </Typography>
                                </Box>
                                <Box sx={{ ...iconContainerProps, color: 'error.main' }}>
                                    <AdminIcon />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={cardGridProps}>
                                <Box sx={textContainerProps}>
                                    <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('common.activeUsers', 'Active Users')}
                                    </Typography>
                                    <Typography variant="h4" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {stats.activeUsers}
                                    </Typography>
                                </Box>
                                <Box sx={{ ...iconContainerProps, color: 'success.main' }}>
                                    <CheckIcon />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={cardGridProps}>
                                <Box sx={textContainerProps}>
                                    <Typography color="textSecondary" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {t('common.averagePrivileges', 'Avg. Privileges')}
                                    </Typography>
                                    <Typography variant="h4" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                        {stats.avgPrivileges}
                                    </Typography>
                                </Box>
                                <Box sx={{ ...iconContainerProps, color: 'info.main' }}>
                                    <EditIcon />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters Paper */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <FilterList />
                    <Typography variant="h6">{t('filters', 'Filters')}</Typography>
                    {hasActiveFilters && (
                        <Chip
                            label={t('common.filtersApplied', 'Filters applied')}
                            size="small"
                            color="info"
                            variant="outlined"
                        />
                    )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
                    {/* Search */}
                    <TextField
                        label={t('common.search', 'Search')}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        size="small"
                        sx={{ minWidth: 200 }}
                        placeholder={t('users.searchPlaceholder', 'Name, username, email...')}
                    />

                    {/* Status Filter */}
                    <Autocomplete
                        options={STATUS_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={STATUS_OPTIONS.find(opt => opt.value === filters.status) || STATUS_OPTIONS[0]}
                        onChange={(event, newValue) => handleFilterChange('status', newValue?.value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.status', 'Status')} size="small" />
                        )}
                        sx={{ minWidth: 150 }}
                        disableClearable
                    />

                    {/* User Type Filter */}
                    <Autocomplete
                        options={USER_TYPE_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={USER_TYPE_OPTIONS.find(opt => opt.value === filters.userType) || USER_TYPE_OPTIONS[0]}
                        onChange={(event, newValue) => handleFilterChange('userType', newValue?.value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('common.userType', 'User Type')} size="small" />
                        )}
                        sx={{ minWidth: 180 }}
                        disableClearable
                    />

                    {/* Branch Filter */}
                    {isSuperAdmin && (
                        <Autocomplete
                            options={branches}
                            getOptionLabel={(option) => option.name || ''}
                            value={filters.branch}
                            onChange={(event, newValue) => handleFilterChange('branch', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label={t('common.branch', 'Branch')} size="small" />
                            )}
                            sx={{ minWidth: 180 }}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                        />
                    )}

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={handleClearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            {t('common.clearFilters', 'Clear')}
                        </Button>
                    )}
                </Stack>
            </Paper>

            {/* Active Filters Display */}
            {hasActiveFilters && (
                <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    {filters.search && (
                        <Chip
                            label={`${t('common.search', 'Search')}: ${filters.search}`}
                            size="small"
                            onDelete={() => handleFilterChange('search', '')}
                        />
                    )}
                    {filters.status && (
                        <Chip
                            label={`${t('common.status', 'Status')}: ${STATUS_OPTIONS.find(o => o.value === filters.status)?.label}`}
                            size="small"
                            color={filters.status === 'active' ? 'success' : 'default'}
                            onDelete={() => handleFilterChange('status', '')}
                        />
                    )}
                    {filters.userType && (
                        <Chip
                            label={`${t('common.userType', 'User Type')}: ${USER_TYPE_OPTIONS.find(o => o.value === filters.userType)?.label}`}
                            size="small"
                            color={filters.userType === 'superAdmin' ? 'error' : 'default'}
                            onDelete={() => handleFilterChange('userType', '')}
                        />
                    )}
                    {filters.branch && (
                        <Chip
                            label={`${t('common.branch', 'Branch')}: ${filters.branch.name}`}
                            size="small"
                            onDelete={() => handleFilterChange('branch', null)}
                        />
                    )}
                </Box>
            )}

            {/* Results Info */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('common.showing', 'Showing')} {totalUsers === 0 ? 0 : Math.min(page * rowsPerPage + 1, totalUsers)}-{Math.min((page + 1) * rowsPerPage, totalUsers)} {t('common.of', 'of')} {totalUsers} {t('common.items', 'items')}
            </Typography>

            {/* Table / No Content */}
            {users.length === 0 && !loading ? (
                <Paper sx={{ p: 4 }}>
                    <NoContent
                        icon={<PersonIcon sx={{ fontSize: 64 }} />}
                        title={
                            hasActiveFilters
                                ? t('users.noFilterResults', 'No users match your filters')
                                : t('users.noUsersTitle', 'No Users Found')
                        }
                        description={
                            hasActiveFilters
                                ? t('users.tryAdjustingFilters', 'Try adjusting your filter criteria')
                                : t('users.noUsersDescription', 'Start by adding your first user.')
                        }
                        action={
                            canCreate && !hasActiveFilters && (
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    onClick={handleOpenAddUserDialog}
                                >
                                    {t('users.add', 'Add User')}
                                </Button>
                            )
                        }
                    />
                </Paper>
            ) : (
                <UsersTable
                    users={users}
                    loading={loading}
                    currentUser={currentUser}
                    isSuperAdmin={isSuperAdmin}
                    canEdit={canEdit}
                    branches={branches}
                    onEditPrivileges={handleOpenPrivilegeDialog}
                    onMenuOpen={handleMenuOpen}
                    onBranchChange={handleBranchChange}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={totalUsers}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    filters={filters}
                    isRTL={isRTL}
                />
            )}

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { minWidth: 200 } }}
            >
                <MenuItem onClick={() => handleOpenPasswordDialog(menuUser)}>
                    <ListItemIcon>
                        <KeyIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText>{t('users.resetPassword', 'Reset Password')}</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={() => handleToggleStatus(menuUser)}>
                    <ListItemIcon>
                        {menuUser?.isActive ? (
                            <BlockIcon fontSize="small" color="warning" />
                        ) : (
                            <EnableIcon fontSize="small" color="success" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {menuUser?.isActive ? t('common.disable', 'Disable') : t('common.enable', 'Enable')}
                    </ListItemText>
                </MenuItem>

                {canDelete && (
                    <MenuItem onClick={() => handleDelete(menuUser)} sx={{ color: 'error.main' }}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>{t('common.delete', 'Delete')}</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Dialogs */}
            {selectedUser && isPrivilegeDialogOpen && (
                <PrivilegeDialog
                    open={isPrivilegeDialogOpen}
                    onClose={handleClosePrivilegeDialog}
                    user={selectedUser}
                    modules={privilegeModules || {}}
                />
            )}

            {selectedUser && isPasswordDialogOpen && (
                <ChangePasswordDialog
                    open={isPasswordDialogOpen}
                    onClose={handleClosePasswordDialog}
                    user={selectedUser}
                />
            )}

            {isAddUserDialogOpen && (
                <AddUserDialog
                    open={isAddUserDialogOpen}
                    onClose={handleCloseAddUserDialog}
                    branches={branches}
                />
            )}

            <ConfirmDialog
                open={openConfirm}
                title={confirmDetails.title}
                message={confirmDetails.message}
                onConfirm={confirmDetails.onConfirm}
                onClose={() => {
                    setOpenConfirm(false);
                    setMenuUser(null);
                }}
                loading={confirmDetails.loading}
                confirmText={confirmDetails.confirmText}
                cancelText={t('common.cancel', 'Cancel')}
            />
        </Box>
    );
};

export default PrivilegeManager;